import React, {useCallback, useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import DataTable from 'react-data-table-component';
import {getReport, ReportFilterValues, ReportPayload} from '@app/services/reports';
import {getAllActiveFranchise} from '@app/services/franchise';
import KpiStrip from './components/KpiStrip';
import ReportFilters, {FranchiseOption} from './components/ReportFilters';
import {
  ChartCard,
  ReportDonutChart,
  ReportHorizontalBarChart
} from './components/ReportChart';
import useReportExport from './hooks/useReportExport';
import {reportTableStyles} from './components/tableStyles';
import {humanise} from './components/labels';
import {findReportBySlug} from './reportCatalogue';

/**
 * ORD2 — Item Demand.
 *
 * Measured in UNITS, deliberately. Materials Revenue covers the same orders in
 * pounds and the two rank differently — sets have the most order lines but rank
 * third by value — so demand and revenue are genuinely separate questions.
 *
 * Top items lead, because "which items move" is the question; the type mix is
 * secondary context.
 */

const SLUG = 'item-demand';
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({length: 5}, (_, i) => CURRENT_YEAR - i);

const number = (v: any) => new Intl.NumberFormat('en-GB').format(Number(v) || 0);
const money = (v: any) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2
  }).format(Number(v) || 0);

const ItemDemand = () => {
  const definition = findReportBySlug(SLUG);

  const [payload, setPayload] = useState<ReportPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [franchises, setFranchises] = useState<FranchiseOption[]>([]);

  const [filters, setFilters] = useState<ReportFilterValues>({
    mode: 'year',
    year: CURRENT_YEAR,
    period: 'full',
    page: 1,
    limit: 25
  });
  const [applied, setApplied] = useState<ReportFilterValues>(filters);

  const {exportToExcel, exporting} = useReportExport('Item Demand');

  const load = useCallback(async (active: ReportFilterValues) => {
    setLoading(true);
    setError(null);
    const response = await getReport(SLUG, active);
    if (response && response.success) {
      setPayload(response);
    } else {
      setPayload(null);
      setError(response?.message || 'The report could not be loaded.');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load(applied);
  }, [applied, load]);

  useEffect(() => {
    (async () => {
      const response = await getAllActiveFranchise();
      if (response?.success === true && Array.isArray(response.collections)) {
        setFranchises(
          response.collections.map((f: any) => ({value: f.id, label: f.name}))
        );
      }
    })();
  }, []);

  const collections = payload?.collections;
  const topItems = collections?.series?.top_items || [];
  const byItemType = collections?.series?.by_item_type || [];
  const detail = collections?.detail;

  const columns = [
    {name: 'Item', selector: (row: any) => row.item || '---', wrap: true},
    {name: 'Type', selector: (row: any) => humanise(row.item_type) || '---', width: '170px'},
    {name: 'Units', width: '110px', right: true, selector: (row: any) => number(row.units)},
    {name: 'Orders', width: '100px', right: true, selector: (row: any) => number(row.orders)},
    {name: 'Lines', width: '100px', right: true, selector: (row: any) => number(row.lines)},
    {name: 'Value', width: '130px', right: true, selector: (row: any) => money(row.value)}
  ];

  return (
    <div>
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-7">
              <h1>{definition?.name || 'Item Demand'}</h1>
              <p className="text-muted mb-0">{definition?.question}</p>
            </div>
            <div className="col-sm-5">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item"><a href="/">Home</a></li>
                <li className="breadcrumb-item"><Link to="/reports">Reports</Link></li>
                <li className="breadcrumb-item active">Item Demand</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <ReportFilters
            values={filters}
            onChange={setFilters}
            onApply={() => setApplied({...filters, limit: applied.limit, page: 1})}
            onReset={(defaults) => {
              setFilters(defaults);
              setApplied(defaults);
            }}
            onExport={() => exportToExcel(payload, SLUG, applied)}
            exporting={exporting}
            franchises={franchises}
            years={YEARS}
            intervalLabel={collections?.filters_applied?.interval_label}
            show={{franchise: true, itemType: true, subject: true}}
            disabled={loading}
          />

          {error && (
            <div className="alert alert-warning">
              <i className="fas fa-exclamation-triangle mr-2" />
              {error}
            </div>
          )}

          <KpiStrip kpis={collections?.kpis || []} loading={loading} />

          <div className="row">
            <div className="col-lg-7">
              <ChartCard
                title="Most-ordered items"
                subtitle="By units, which is what print planning and stock need"
                loading={loading}
                isEmpty={!topItems.length}
                height={430}>
                <ReportHorizontalBarChart
                  data={topItems}
                  labelKey="item"
                  valueKey="units"
                  currency={false}
                  height={390}
                  labelWidth={130}
                  colour="#0F8F82"
                />
              </ChartCard>
            </div>
            <div className="col-lg-5">
              <ChartCard
                title="Units by item type"
                subtitle="Share of everything ordered"
                loading={loading}
                isEmpty={!byItemType.length}
                height={430}>
                <ReportDonutChart
                  data={byItemType}
                  nameKey="item_type"
                  valueKey="units"
                  height={390}
                />
              </ChartCard>
            </div>
          </div>

          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h3 className="card-title text-capitalize">All items, ranked by units</h3>
            </div>
            <div className="card-body p-0">
              <DataTable
                columns={columns}
                customStyles={reportTableStyles}
                data={detail?.rows || []}
                progressPending={loading}
                pagination
                paginationServer
                paginationTotalRows={detail?.total || 0}
                paginationDefaultPage={applied.page || 1}
                paginationPerPage={applied.limit || 25}
                paginationRowsPerPageOptions={[25, 50, 100]}
                onChangePage={(page) => setApplied({...applied, page})}
                onChangeRowsPerPage={(limit, page) =>
                  setApplied({...applied, limit, page})
                }
                noDataComponent={
                  <div className="text-muted py-4">No items match these filters.</div>
                }
                highlightOnHover
                dense
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ItemDemand;
